import { z } from 'zod';
import { FieldRefSchema } from './query/ast';
import { SlaKindSchema } from './sla';

/*
 * One timer mechanism (ADR-0019 §1): every due time is a `timer` row swept by the timers
 * worker on the scheduler cadence. `dedupe_key` keeps one live timer per subject (partial
 * unique index: fired and cancelled rows free the key); `payload` is typed per `kind`.
 */

export const TIMER_KINDS = [
  'sla',
  'date_field',
  'notify_repeat',
  'schedule',
  'approval_expiry',
  // Bolt 8 H3: the next webhook delivery attempt (ADR-0019 §6); fired by the timers worker,
  // which hands it to `retryDelivery` (`packages/jobs/src/webhooks-dispatch.ts`) instead of
  // emitting an `automation.due` event like every other kind.
  'webhook_retry',
] as const;
export const TimerKindSchema = z.enum(TIMER_KINDS);
export type TimerKind = z.infer<typeof TimerKindSchema>;

export const TIMER_DEDUPE_KEY_MAX = 200;

/** `sla`: a running `ticket_sla` reaches `due_at` → `sla.breached`. */
export const SlaTimerPayloadSchema = z.object({
  ticketSlaId: z.uuid(),
  ticketId: z.uuid(),
  policyId: z.uuid(),
  slaKind: SlaKindSchema,
});

/** `date_field`: `field + offsetDays` of one ticket for one rule → `automation.due`. */
export const DateFieldTimerPayloadSchema = z.object({
  ruleId: z.uuid(),
  ticketId: z.uuid(),
  field: FieldRefSchema,
  /** The date value the timer was armed from; a changed value re-arms (replaces) the row. */
  fieldValue: z.string(),
  scheduledFor: z.iso.datetime(),
});

/** `notify_repeat`: the next repeat of a `notify` action until acknowledged (S-08-06). */
export const NotifyRepeatTimerPayloadSchema = z.object({
  ruleId: z.uuid(),
  ticketId: z.uuid(),
  /** Index of the `notify` action in `automation_rule.actions`, re-read at fire time. */
  actionIndex: z.int().min(0),
  /** 1-based number of the repeat this timer sends (0 = the initial delayed send). */
  repeat: z.int().min(0),
  maxRepeats: z.int().min(1),
  /** The event that started the chain; repeats carry it as `causationId`. */
  originEventId: z.uuid(),
});

/** `schedule`: next occurrence of a cron rule → `automation.due`, then re-armed. */
export const ScheduleTimerPayloadSchema = z.object({
  ruleId: z.uuid(),
  scheduledFor: z.iso.datetime(),
  timeZone: z.string().min(1),
});

/** `approval_expiry`: the email token of one approval expires (`token_expires_at`). */
export const ApprovalExpiryTimerPayloadSchema = z.object({
  approvalId: z.uuid(),
  requestId: z.uuid(),
  ticketId: z.uuid(),
});

/** `webhook_retry`: the next attempt of one `webhook_delivery` is due (S-08-07). */
export const WebhookRetryTimerPayloadSchema = z.object({
  deliveryId: z.uuid(),
});

export const TIMER_PAYLOAD_SCHEMAS = {
  sla: SlaTimerPayloadSchema,
  date_field: DateFieldTimerPayloadSchema,
  notify_repeat: NotifyRepeatTimerPayloadSchema,
  schedule: ScheduleTimerPayloadSchema,
  approval_expiry: ApprovalExpiryTimerPayloadSchema,
  webhook_retry: WebhookRetryTimerPayloadSchema,
} as const satisfies Record<TimerKind, z.ZodType>;

export type TimerPayloadOf<K extends TimerKind> = z.infer<(typeof TIMER_PAYLOAD_SCHEMAS)[K]>;
export type TimerPayload = { [K in TimerKind]: TimerPayloadOf<K> }[TimerKind];

const timerBase = {
  id: z.uuid(),
  dueAt: z.iso.datetime(),
  ruleId: z.uuid().nullable(),
  ticketId: z.uuid().nullable(),
  firedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  dedupeKey: z.string().min(1).max(TIMER_DEDUPE_KEY_MAX),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
};

/** A `timer` row; `kind` selects the payload shape. */
export const TimerSchema = z.discriminatedUnion('kind', [
  z.object({ ...timerBase, kind: z.literal('sla'), payload: SlaTimerPayloadSchema }),
  z.object({ ...timerBase, kind: z.literal('date_field'), payload: DateFieldTimerPayloadSchema }),
  z.object({
    ...timerBase,
    kind: z.literal('notify_repeat'),
    payload: NotifyRepeatTimerPayloadSchema,
  }),
  z.object({ ...timerBase, kind: z.literal('schedule'), payload: ScheduleTimerPayloadSchema }),
  z.object({
    ...timerBase,
    kind: z.literal('approval_expiry'),
    payload: ApprovalExpiryTimerPayloadSchema,
  }),
  z.object({
    ...timerBase,
    kind: z.literal('webhook_retry'),
    payload: WebhookRetryTimerPayloadSchema,
  }),
]);
export type Timer = z.infer<typeof TimerSchema>;

/** Dedupe keys, one live timer per subject. Build them here so every writer agrees. */
export const timerDedupeKey = {
  sla: (ticketSlaId: string): string => `sla:${ticketSlaId}`,
  dateField: (ruleId: string, ticketId: string): string => `date_field:${ruleId}:${ticketId}`,
  notifyRepeat: (ruleId: string, ticketId: string, actionIndex: number): string =>
    `notify_repeat:${ruleId}:${ticketId}:${actionIndex}`,
  schedule: (ruleId: string): string => `schedule:${ruleId}`,
  approvalExpiry: (approvalId: string): string => `approval_expiry:${approvalId}`,
  webhookRetry: (deliveryId: string): string => `webhook_retry:${deliveryId}`,
} as const;
